/**
 * Alcove Tolerance Calculator V2 — TCS filler fit, scaled SVG (front elevation only)
 * calcAlcove(inputs) -> result; renderElevationSvg
 */
(function () {
  'use strict';

  var TCS_REDUCTION = 100;
  var TCS_FILLER_EACH = 50;
  var SHADOW_GAP = 9;
  var SKILL_CLEARANCE = { trade: 5, experienced: 8, beginner: 13 };
  var OPENING_PX = 312;

  function getClearanceTotal(skill) {
    return SKILL_CLEARANCE[skill] || SKILL_CLEARANCE.experienced;
  }

  function calcAlcove(inputs) {
    var minW = parseFloat(inputs.minWidth) || 1600;
    var maxW = inputs.maxWidth ? parseFloat(inputs.maxWidth) : minW;
    var min = Math.min(minW, maxW);
    var max = Math.max(minW, maxW);
    var variation = max - min;
    var finish = inputs.finish || 'tcs_filler';
    var skill = inputs.skill || 'trade';
    var refWidthChoice = inputs.refWidth || 'max';

    var result = {
      minWidth: min,
      maxWidth: max,
      variation: Math.round(variation * 10) / 10,
      referenceWidth: min,
      carcassWidth: 0,
      fillerEachSide: 0,
      clearance: 0,
      scribe: 0,
      shadowGap: 0,
      remainingClearanceTightestEachSide: null,
      warnings: [],
      riskLevel: 'low',
      method: 'One-piece carcass'
    };

    if (finish === 'tcs_filler') {
      result.fillerEachSide = TCS_FILLER_EACH;
      result.referenceWidth = (skill === 'beginner') ? min : (refWidthChoice === 'min' ? min : max);
      result.carcassWidth = Math.round(result.referenceWidth - TCS_REDUCTION);

      if (result.referenceWidth === max && min < max) {
        var tightTotal = min - result.carcassWidth - 2 * TCS_FILLER_EACH;
        result.remainingClearanceTightestEachSide = Math.round((tightTotal / 2) * 10) / 10;
        if (result.remainingClearanceTightestEachSide < 0) {
          result.warnings.push('Based on MAX width, fillers will likely need trimming to fit the tightest point.');
        }
      }

      if (variation > 80) {
        result.warnings.push('Severely out-of-square/bowed opening — consider scribe fit or split install.');
      } else if (variation > 40) {
        result.warnings.push('Large variation detected — fillers may need trimming.');
      }

      result.riskLevel = variation > 80 ? 'high' : (variation > 40 ? 'medium' : 'low');
      result.method = result.riskLevel === 'high' && skill === 'beginner' ? 'Split carcass or fitter assistance recommended' : 'One-piece ok; fillers fixed to cabinet sides';
    } else if (finish === 'scribe') {
      var clearanceTotal = getClearanceTotal(skill);
      result.referenceWidth = min;
      result.carcassWidth = Math.round(min - clearanceTotal);
      result.clearance = Math.round((clearanceTotal / 2) * 10) / 10;
      result.scribe = Math.max(10, Math.min(25, variation / 2 + 6));
      result.riskLevel = variation > 15 ? 'high' : (variation > 8 ? 'medium' : 'low');
      result.method = result.riskLevel === 'high' && skill === 'beginner' ? 'Split carcass or fitter assistance recommended' : (result.riskLevel === 'medium' ? 'One-piece ok; allow scribe time' : 'One-piece carcass');
    } else if (finish === 'shadow') {
      result.referenceWidth = min;
      result.carcassWidth = Math.round(min - 2 * SHADOW_GAP);
      result.shadowGap = SHADOW_GAP;
      result.riskLevel = variation > 15 ? 'high' : (variation > 8 ? 'medium' : 'low');
      result.method = 'One-piece; visible gap each side';
    } else if (finish === 'loose') {
      var looseClear = getClearanceTotal(skill) + 10;
      result.referenceWidth = min;
      result.carcassWidth = Math.round(min - looseClear - 20);
      result.clearance = Math.round((looseClear / 2 + 10) * 10) / 10;
      result.riskLevel = variation > 15 ? 'high' : (variation > 8 ? 'medium' : 'low');
      result.method = 'One-piece; generous clearance for easy install';
    } else if (finish === 'filler_generic') {
      var filler = Math.max(15, Math.min(30, variation / 2 + 12));
      var clearTotal = getClearanceTotal(skill);
      result.referenceWidth = min;
      result.carcassWidth = Math.round(min - clearTotal - 2 * filler);
      result.fillerEachSide = Math.round(filler * 10) / 10;
      result.riskLevel = variation > 40 ? 'high' : (variation > 20 ? 'medium' : 'low');
      result.method = 'One-piece; calculated filler panels';
    }

    return result;
  }

  function renderElevationSvg(result, inputs) {
    var min = result.minWidth || 1600;
    var carcass = result.carcassWidth || 1400;
    var scale = OPENING_PX / min;
    var unitW = carcass * scale;
    var gapPx = (OPENING_PX - unitW) / 2;
    var gapW = gapPx > 0 ? Math.max(1, gapPx) : 0;

    var leftGap = gapW > 0 ? '<rect x="44" y="60" width="' + gapW + '" height="96" fill="rgba(250,192,94,0.08)" stroke="rgba(250,192,94,0.3)" stroke-width="1" stroke-dasharray="4 2"/>' : '';
    var rightGap = gapW > 0 ? '<rect x="' + (44 + gapPx + unitW) + '" y="60" width="' + gapW + '" height="96" fill="rgba(250,192,94,0.08)" stroke="rgba(250,192,94,0.3)" stroke-width="1" stroke-dasharray="4 2"/>' : '';
    var gapLabel = gapW > 2 ? '<text x="' + (44 + gapW/2) + '" y="108" fill="var(--cablab-accent)" font-size="10" text-anchor="middle" transform="rotate(-90 ' + (44 + gapW/2) + ' 108)">Gap</text>' : '';

    return '<svg viewBox="0 0 400 180" xmlns="http://www.w3.org/2000/svg" class="cablab-alcove__svg-inner">' +
      '<defs><linearGradient id="alcove-wall-grad" x1="0%" y1="0%" x2="100%" y2="0%">' +
      '<stop offset="0%" stop-color="rgba(255,255,255,0.06)"/><stop offset="100%" stop-color="rgba(255,255,255,0.02)"/>' +
      '</linearGradient></defs>' +
      '<path d="M 40 20 L 40 160 L 44 160 L 44 20 Z" fill="url(#alcove-wall-grad)" stroke="rgba(255,255,255,0.15)" stroke-width="1"/>' +
      '<path d="M ' + (360 - 4) + ' 20 L ' + (360 - 4) + ' 160 L 360 160 L 360 20 Z" fill="url(#alcove-wall-grad)" stroke="rgba(255,255,255,0.15)" stroke-width="1"/>' +
      '<rect x="40" y="156" width="320" height="8" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.1)" stroke-width="1"/>' +
      '<rect x="' + (44 + gapPx) + '" y="60" width="' + Math.round(unitW) + '" height="96" rx="2" fill="rgba(250,192,94,0.15)" stroke="var(--cablab-accent)" stroke-width="2"/>' +
      leftGap + rightGap +
      gapLabel +
      '<text x="' + (44 + gapPx + unitW/2) + '" y="150" fill="var(--cablab-text-muted)" font-size="10" text-anchor="middle">Carcass ' + result.carcassWidth + 'mm</text>' +
      '</svg>';
  }

  function init(container) {
    if (!container) return;

    var minW = container.querySelector('[data-alcove-min-width]');
    var maxW = container.querySelector('[data-alcove-max-width]');
    var finish = container.querySelector('[data-alcove-finish]');
    var skill = container.querySelector('[data-alcove-skill]');
    var refWrap = container.querySelector('[data-alcove-ref-width-wrap]');
    var refRadios = container.querySelectorAll('[data-alcove-ref-width]');
    var svgEl = container.querySelector('[data-alcove-svg]');

    var outWidth = container.querySelector('[data-alcove-result-width]');
    var outFiller = container.querySelector('[data-alcove-result-filler]');
    var outRefWidth = container.querySelector('[data-alcove-result-ref-width]');
    var outClearance = container.querySelector('[data-alcove-result-clearance]');
    var outScribe = container.querySelector('[data-alcove-result-scribe]');
    var outShadowGap = container.querySelector('[data-alcove-result-shadow-gap]');
    var outLoose = container.querySelector('[data-alcove-result-loose]');
    var outGenericFiller = container.querySelector('[data-alcove-result-generic-filler]');
    var outMethod = container.querySelector('[data-alcove-result-method]');
    var outTcsGuidance = container.querySelector('[data-alcove-output-tcs-guidance]');
    var outTcs = container.querySelector('[data-alcove-output-tcs]');
    var outTightestWrap = container.querySelector('[data-alcove-output-tightest]');
    var outTightest = container.querySelector('[data-alcove-result-tightest]');
    var outScribeCard = container.querySelector('[data-alcove-output-scribe]');
    var outShadow = container.querySelector('[data-alcove-output-shadow]');
    var outLooseCard = container.querySelector('[data-alcove-output-loose]');
    var outGeneric = container.querySelector('[data-alcove-output-generic]');
    var outVariation = container.querySelector('[data-alcove-result-variation]');
    var outWarnings = container.querySelector('[data-alcove-result-warnings]');
    var outRisk = container.querySelector('[data-alcove-result-risk]');

    function getRefWidth() {
      var r = container.querySelector('[data-alcove-ref-width]:checked');
      return r ? r.value : 'max';
    }

    function showMode(el) {
      [outTcs, outScribeCard, outShadow, outLooseCard, outGeneric].forEach(function (c) {
        if (c) c.style.display = 'none';
      });
      if (el) el.style.display = '';
    }

    function run() {
      var opt = {
        minWidth: minW && minW.value,
        maxWidth: maxW && maxW.value,
        finish: finish && finish.value,
        skill: skill && skill.value,
        refWidth: getRefWidth()
      };
      var r = calcAlcove(opt);

      if (refWrap) {
        refWrap.style.display = (opt.finish === 'tcs_filler' && opt.skill !== 'beginner') ? '' : 'none';
      }

      if (outWidth) outWidth.textContent = r.carcassWidth;
      if (outMethod) outMethod.textContent = r.method;

      if (opt.finish === 'tcs_filler') {
        showMode(outTcs);
        if (outFiller) outFiller.textContent = '50';
        if (outRefWidth) outRefWidth.textContent = r.referenceWidth === r.minWidth ? 'MIN (' + Math.round(r.minWidth) + 'mm)' : 'MAX (' + Math.round(r.maxWidth) + 'mm)';
        if (outTightestWrap) outTightestWrap.style.display = (r.remainingClearanceTightestEachSide !== null) ? '' : 'none';
        if (outTightest) outTightest.textContent = r.remainingClearanceTightestEachSide !== null ? r.remainingClearanceTightestEachSide : '—';
        if (outTcsGuidance) outTcsGuidance.style.display = '';
      } else if (opt.finish === 'scribe') {
        showMode(outScribeCard);
        if (outClearance) outClearance.textContent = r.clearance;
        if (outScribe) outScribe.textContent = r.scribe;
        if (outTcsGuidance) outTcsGuidance.style.display = 'none';
      } else if (opt.finish === 'shadow') {
        showMode(outShadow);
        if (outShadowGap) outShadowGap.textContent = r.shadowGap;
        if (outTcsGuidance) outTcsGuidance.style.display = 'none';
      } else if (opt.finish === 'loose') {
        showMode(outLooseCard);
        if (outLoose) outLoose.textContent = r.clearance + 'mm each side for easy install';
        if (outTcsGuidance) outTcsGuidance.style.display = 'none';
      } else if (opt.finish === 'filler_generic') {
        showMode(outGeneric);
        if (outGenericFiller) outGenericFiller.textContent = r.fillerEachSide;
        if (outTcsGuidance) outTcsGuidance.style.display = 'none';
      }

      if (outVariation) {
        outVariation.textContent = r.variation > 0 ? 'Alcove variation: ' + r.variation + ' mm' : '';
        outVariation.style.display = r.variation > 0 ? '' : 'none';
      }
      if (outWarnings) {
        outWarnings.innerHTML = r.warnings.length ? '<ul><li>' + r.warnings.join('</li><li>') + '</li></ul>' : '';
        outWarnings.style.display = r.warnings.length ? '' : 'none';
      }
      if (outRisk) {
        outRisk.textContent = 'Risk: ' + r.riskLevel.charAt(0).toUpperCase() + r.riskLevel.slice(1);
        outRisk.className = 'cablab-alcove__risk cablab-alcove__risk--' + r.riskLevel;
      }

      if (svgEl) svgEl.innerHTML = renderElevationSvg(r, opt);
    }

    [minW, maxW, finish, skill].forEach(function (el) {
      if (el) { el.addEventListener('input', run); el.addEventListener('change', run); }
    });
    refRadios.forEach(function (r) {
      if (r) r.addEventListener('change', run);
    });

    run();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('[data-cablab-alcove-tolerance]').forEach(init);
    });
  } else {
    document.querySelectorAll('[data-cablab-alcove-tolerance]').forEach(init);
  }
})();
